import React, { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import {
    Button,
    CircularLoader,
    NoticeBox,
    InputField,
    IconEdit16,
    IconDelete16
} from '@dhis2/ui';
import { STAFF_TEI_TYPE, STAFF_ATTRIBUTES } from '../api/constants';

const StaffList = ({ onEdit, onDelete }) => {
    const engine = useDataEngine();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [staffMembers, setStaffMembers] = useState([]);
    const [currentOrgUnit, setCurrentOrgUnit] = useState(null);
    const [search, setSearch] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                console.log('🔍 Starting staff data fetch...');

                // 1. Get current user's org unit - FIXED: Add dataViewOrganisationUnits
                const { me } = await engine.query({
                    me: {
                        resource: 'me',
                        params: {
                            fields: 'organisationUnits[id,name,level],dataViewOrganisationUnits[id,name,level]'
                        }
                    }
                });

                if (!me.organisationUnits?.length && !me.dataViewOrganisationUnits?.length) {
                    throw new Error('No organization units assigned to current user');
                }

                // Combine both types of org units
                const allUserOrgUnits = [
                    ...(me.organisationUnits || []),
                    ...(me.dataViewOrganisationUnits || [])
                ];

                const primaryOrgUnit = allUserOrgUnits[0];
                setCurrentOrgUnit(primaryOrgUnit);

                console.log('📍 All user org units:', JSON.stringify(allUserOrgUnits, null, 2));
                console.log('🎯 Primary org unit (index 0):', primaryOrgUnit.name, '- ID:', primaryOrgUnit.id, '- Level:', primaryOrgUnit.level);

                // 🔍 DEBUG: Check if SMXU0T5baX9 is in user's org units
                const targetOrgUnitId = 'SMXU0T5baX9';
                const hasTargetOrgUnit = allUserOrgUnits.some(ou => ou.id === targetOrgUnitId);
                console.log('🔍 Looking for target org unit:', targetOrgUnitId);
                console.log('✅ Target org unit found in user\'s org units:', hasTargetOrgUnit);
                if (hasTargetOrgUnit) {
                    console.log('🎯 Should filter by:', allUserOrgUnits.find(ou => ou.id === targetOrgUnitId));
                }

                // 2. Fetch ALL users
                console.log('📋 Fetching ALL users...');
                const usersResponse = await engine.query({
                    users: {
                        resource: 'users',
                        params: {
                            fields: 'id,firstName,surname,email,phoneNumber,userCredentials[username,userRoles[id]],organisationUnits[id,name,level],attributeValues[attribute,value]',
                            pageSize: 1000,
                            filter: 'userCredentials.disabled:eq:false'
                        }
                    }
                });

                const allUsers = usersResponse.users?.users || [];
                console.log('✅ All users fetched:', allUsers.length);

                // 3. CRITICAL FIX: Fetch TEIs with ACCESSIBLE mode and trackedEntityType
                console.log('📋 Fetching TEIs...');
                const teisResponse = await engine.query({
                    teis: {
                        resource: 'trackedEntityInstances',
                        params: {
                            trackedEntityType: STAFF_TEI_TYPE,
                            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
                            ou: primaryOrgUnit.id,
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1000,
                            filter: `${STAFF_ATTRIBUTES.USERNAME}:!EQ:null`
                        }
                    }
                });

                if (!isMounted) return;

                const allTeis = teisResponse.teis?.trackedEntityInstances || [];
                console.log('✅ TEIs found (ACCESSIBLE mode):', allTeis.length);

                if (allTeis.length === 0) {
                    // Try DESCENDANTS as fallback
                    console.log('🔄 No TEIs in ACCESSIBLE mode, trying DESCENDANTS...');
                    try {
                        const teisDescendants = await engine.query({
                            teis: {
                                resource: 'trackedEntityInstances',
                                params: {
                                    trackedEntityType: STAFF_TEI_TYPE,
                                    fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
                                    ou: primaryOrgUnit.id,
                                    ouMode: 'DESCENDANTS',
                                    pageSize: 1000
                                }
                            }
                        });

                        if (teisDescendants.teis?.trackedEntityInstances?.length > 0) {
                            console.log('✅ TEIs found (DESCENDANTS mode):', teisDescendants.teis.trackedEntityInstances.length);
                            allTeis.push(...teisDescendants.teis.trackedEntityInstances);
                        }
                    } catch (fallbackError) {
                        console.warn('⚠️ DESCENDANTS mode also failed:', fallbackError);
                    }
                }

                // 4. Get org unit hierarchy for filtering
                console.log('🏢 Building org unit hierarchy...');
                const hierarchyOrgUnits = new Set();
                
                // 🔍 DEBUG: Track which org units are being added from TEIs
                const teiOrgUnits = [];
                allTeis.forEach(tei => {
                    if (tei.orgUnit) {
                        teiOrgUnits.push(tei.orgUnit);
                        hierarchyOrgUnits.add(tei.orgUnit);
                    }
                });
                console.log('📍 Org units from TEIs:', teiOrgUnits.length, teiOrgUnits.slice(0, 10));
                
                // 🔍 DEBUG: Add filtering by ALL user org units, not just primary
                console.log('🔍 Querying hierarchy for ALL user org units:', allUserOrgUnits.map(ou => ({ id: ou.id, name: ou.name, level: ou.level })));
                
                // Query for each user org unit
                for (const userOu of allUserOrgUnits) {
                    console.log('🔍 Querying hierarchy for:', userOu.id, '-', userOu.name, '- Level:', userOu.level);
                    try {
                        const hierarchyResponse = await engine.query({
                            organisationUnits: {
                                resource: 'organisationUnits',
                                params: {
                                    fields: 'id',
                                    filter: `path:like:${userOu.id}`,
                                    paging: false
                                }
                            }
                        });
                        console.log('✅ Found', hierarchyResponse.organisationUnits?.length || 0, 'org units under', userOu.name);
                        hierarchyResponse.organisationUnits?.forEach(ou => {
                            hierarchyOrgUnits.add(ou.id);
                        });
                    } catch (hierarchyError) {
                        console.warn('⚠️ Could not get org unit hierarchy for', userOu.name, ':', hierarchyError);
                    }
                }
                
                console.log('🏢 Total org units in scope:', hierarchyOrgUnits.size);
                console.log('📋 Sample org units in scope:', Array.from(hierarchyOrgUnits).slice(0, 10));

                // 5. Match users with TEIs - DEBUG VERSION
                console.log('🔍 === STARTING USER MATCHING DEBUG ===');
                const inScopeUsers = [];
                const outOfScopeUsers = [];
                
                allUsers.forEach(user => {
                    const userUsername = user.userCredentials?.username;
                    const userTeis = userUsername ? allTeis.filter(tei => {
                        const usernameAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.USERNAME
                        );
                        return usernameAttr?.value === userUsername;
                    }) : [];

                    // Get user's assigned org unit IDs
                    const userOrgUnitIds = [
                        ...(user.organisationUnits?.map(ou => ou.id) || []),
                        ...(user.dataViewOrganisationUnits?.map(ou => ou.id) || [])
                    ];

                    // Get TEI's orgUnit
                    const teiOrgUnit = userTeis[0]?.orgUnit || 'NONE';

                    // Check if user org units are in hierarchy
                    const userOrgUnitsInScope = userOrgUnitIds.filter(id => hierarchyOrgUnits.has(id));
                    const isInScope = userOrgUnitsInScope.length > 0;

                    const userDebug = {
                        username: userUsername,
                        fullName: `${user.firstName || ''} ${user.surname || ''}`,
                        userOrgUnits: user.organisationUnits?.map(ou => ({ id: ou.id, name: ou.name })),
                        teiOrgUnit: teiOrgUnit,
                        hierarchyOrgUnitsCount: hierarchyOrgUnits.size,
                        userOrgUnitsInScope: userOrgUnitsInScope,
                        isInScope: isInScope,
                        isInScopeReason: isInScope 
                            ? `User org units [${userOrgUnitsInScope.join(', ')}] found in hierarchy`
                            : 'User org units NOT in hierarchy'
                    };

                    if (isInScope) {
                        inScopeUsers.push(userDebug);
                        console.log('✅ IN SCOPE:', userDebug);
                    } else {
                        outOfScopeUsers.push(userDebug);
                        // Only log first 20 out of scope to avoid spam
                        if (outOfScopeUsers.length <= 20) {
                            console.log('❌ OUT OF SCOPE:', userDebug);
                        }
                    }
                });
                
                console.log('🔍 === USER MATCHING DEBUG COMPLETE ===');
                console.log('📊 Summary:', {
                    totalUsers: allUsers.length,
                    inScope: inScopeUsers.length,
                    outOfScope: outOfScopeUsers.length
                });
                if (outOfScopeUsers.length > 20) {
                    console.log('... and', outOfScopeUsers.length - 20, 'more out of scope users');
                }

                // Filter to only in-scope users
                const matchedStaff = inScopeUsers.map(userDebug => {
                    const originalUser = allUsers.find(u => u.userCredentials?.username === userDebug.username);
                    const userTeis = originalUser ? allTeis.filter(tei => {
                        const usernameAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.USERNAME
                        );
                        return usernameAttr?.value === originalUser.userCredentials?.username;
                    }) : [];
                    
                    return {
                        ...originalUser,
                        hasTei: userTeis.length > 0,
                        tei: userTeis[0] || null,
                        allTeis: userTeis,
                        isInScope: true
                    };
                });

                console.log('✅ Staff matching completed:', {
                    totalUsers: allUsers.length,
                    usersInScope: matchedStaff.length,
                    usersWithTeI: matchedStaff.filter(s => s.hasTei).length,
                    usersWithoutTeI: matchedStaff.filter(s => !s.hasTei).length
                });

                if (isMounted) {
                    setStaffMembers(matchedStaff);
                }

            } catch (e) {
                console.error('❌ Fetch error:', e);
                if (isMounted) {
                    setError(e.message || 'Failed to load staff data');
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchData();

        return () => { 
            isMounted = false;
        };
    }, [engine, refreshTrigger]);


const refreshStaffList = () => {
    setRefreshTrigger(prev => prev + 1);
    };

    // Filter staff members based on search
    const filteredStaff = staffMembers.filter((staff) => {
        const fullName = `${staff.firstName || ''} ${staff.surname || ''}`.toLowerCase();
        const username = staff.userCredentials?.username?.toLowerCase() || '';
        const email = staff.email?.toLowerCase() || '';
        
        return (
            fullName.includes(search.toLowerCase()) ||
            username.includes(search.toLowerCase()) ||
            email.includes(search.toLowerCase())
        );
    });

    if (loading) {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularLoader small />
            <span style={{ marginLeft: '10px' }}>Loading staff data...</span>
          </div>
        );
    }
    
    if (error) return (
        <NoticeBox error title="Error">
            {error}
            <Button small onClick={() => window.location.reload()} style={{ marginTop: 8 }}>
                Retry
            </Button>
        </NoticeBox>
    );

    return (
        <div style={{ margin: '20px 0' }}>
            <h4>Staff Members ({filteredStaff.length})</h4>
                
            {/* Search input */}
            <InputField
                label="Search Staff"
                value={search}
                onChange={({ value }) => setSearch(value)}
                style={{ marginBottom: '2rem', width: '300px' }}
                placeholder="Search by name, username, or email..."
                helpText={`Searching ${staffMembers.length} staff members in your organization hierarchy`}
            />

            {filteredStaff.length === 0 ? (
                <NoticeBox title="No staff found">
                    {search ? 'No staff members match your search' : 'No staff members found in your organization hierarchy'}
                </NoticeBox>
            ) : (
                <div className="responsive-table" style={{ width: "100%" }}>
                    {/* Header row as flex container */}
                    <div className="table-row header" style={{
                        display: "flex",
                        fontWeight: "bold",
                        borderBottom: "2px solid #ccc",
                        padding: "12px 8px",
                        backgroundColor: "#f0f0f0",
                        marginTop: "20px"
                    }}>
                        <div style={{ flex: 1, padding: "0 8px" }}>Username</div>
                        <div style={{ flex: 2, padding: "0 8px" }}>Name</div>
                        <div style={{ flex: 2, padding: "0 8px" }}>Email</div>
                        <div style={{ flex: 2, padding: "0 8px" }}>Departments</div>
                        <div style={{ flex: 2, padding: "0 8px" }}>Actions</div>
                    </div>

                    {/* Body rows */}
                    {filteredStaff.map(staff => (
                        <div key={staff.id} className="table-row" style={{
                            display: "flex",
                            flexDirection: "row",
                            borderBottom: "1px solid #eee",
                            padding: "12px 8px",
                            alignItems: "center",
                            backgroundColor: staff.hasTei ? 'white' : '#fff9e6'
                        }}>
                            <div style={{ flex: 1, padding: "0 8px", wordBreak: "break-word" }}>
                                {staff.userCredentials?.username}
                            </div>
                            <div style={{ flex: 2, padding: "0 8px", wordBreak: "break-word" }}>
                                {staff.firstName} {staff.surname}
                            </div>
                            <div style={{ flex: 2, padding: "0 8px", wordBreak: "break-word" }}>
                                {staff.email || '—'}
                            </div>
                           
                            <div style={{ flex: 2, padding: "0 8px", wordBreak: "break-word", fontSize: '12px' }}>
                                {staff.organisationUnits?.map(ou => `${ou.name}`).join(', ') || '—'}
                            </div>
                            <div style={{ flex: 2, padding: "0 8px", display: "flex", gap: "8px" }}>
                                <Button 
                                    small 
                                    primary
                                    icon={<IconEdit16 />}
                                    onClick={() => {
                                        if (staff.hasTei && staff.tei) {
                                            // Staff with TEI - pass TEI data with enhanced user info
                                         onEdit({
                                            // For staff with TEI - pass ALL user data
                                            trackedEntityInstance: staff.tei?.trackedEntityInstance,
                                            userId: staff.id,
                                            username: staff.userCredentials?.username,
                                            firstName: staff.firstName || '',
                                            secondName: staff.surname || '',
                                            email: staff.email || '',
                                            phoneNumber: staff.phoneNumber || '',
                                            // Role and department from user credentials
                                            userRole: staff.userCredentials?.userRoles?.[0]?.id || '',
                                            department: '', // You might need to extract this from user.attributeValues
                                            orgUnit: staff.organisationUnits?.[0]?.id || '',
                                            orgUnitName: staff.organisationUnits?.[0]?.name || '',
                                            // Add fallback fields
                                            assignedOu: staff.organisationUnits?.[0]?.id || '',
                                            assignedOuName: staff.organisationUnits?.[0]?.name || '',
                                            // Add TEI attributes
                                            attributes: staff.tei?.attributes || [],
                                            missingTEI: !staff.hasTei,
                                            // Pass position info if available
                                            positionId: staff.positionId || '', // If you have this in staff data
                                            positionDetails: staff.positionDetails || null
                                        });
                                        } else {
                                            // Staff without TEI - create complete structure
                                            onEdit({
                                                // Core identifiers
                                                id: staff.id,
                                                userId: staff.id,
                                                
                                                // User information
                                                username: staff.userCredentials?.username,
                                                firstName: staff.firstName,
                                                secondName: staff.surname,
                                                email: staff.email,
                                                
                                                // Organisation information
                                                orgUnit: staff.organisationUnits?.[0]?.id,
                                                orgUnitName: staff.organisationUnits?.[0]?.name,
                                                
                                                // Empty fields that form expects
                                                phoneNumber: '',
                                                gender: '',
                                                department: '',
                                                userRole: '',
                                                
                                                // Empty attributes array
                                                attributes: [],
                                                
                                                // Flag to indicate this user needs TEI creation
                                                missingTEI: true
                                            });
                                        }
                                    }}
                                >
                                    Edit
                                </Button>
                                {/*
                                <Button 
                                    small 
                                    destructive 
                                    icon={<IconDelete16 />}
                                    onClick={() => {
                                        if (staff.hasTei && staff.tei) {
                                            onDelete(staff.tei.trackedEntityInstance);
                                        } else {
                                            alert('This staff member does not have a TEI record to delete. You can only delete the user account through user management.');
                                        }
                                    }}
                                    disabled={!staff.hasTei}
                                >
                                    Delete
                                </Button>
                                */}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StaffList;
